import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import RootRef from '@material-ui/core/RootRef';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import TextField from 'components/fields/TextField';
import IconClock from 'components/icons/IconClock';

import isDescendant from 'utils/isDescendant';

import Timepicker from './Timepicker';

const styles = theme => ({
  root: {
    position: 'relative',
  },
  popperRoot: {
    position: 'absolute',
    top: 0,
    left: 0,
    margin: 0,
    transform: 'scale(0, 0)',
    zIndex: '1200',
  },
  textFields: {
    position: 'relative',
    display: 'flex',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: 10,
      top: 1,
      bottom: 1,
      left: '50%',
      marginLeft: -5,
      zIndex: 1,
      backgroundColor: theme.palette.grey[100],
    },
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: 1,
      height: 24,
      top: '50%',
      left: '50%',
      marginTop: -12,
      zIndex: 1,
      backgroundColor: theme.palette.primary.A100,
      transform: 'rotate(385deg)',
    },
  },
  textFieldsColorLight: {
    '&:before': {
      backgroundColor: theme.palette.common.white,
    },
  },
  textField: {
    width: 'calc(50% + 5px)',
    marginLeft: -5,
    '& input': {
      paddingLeft: 10,
    },
    '&:first-child': {
      marginLeft: 0,
      '& input': {
        paddingLeft: 15,
        paddingRight: 10,
      },
      '& $textFieldInputRoot': {
        borderRight: 'none',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    '&:last-child': {
      '& $textFieldInputRoot': {
        borderLeft: 'none',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
    },
  },
  textFieldInputRoot: {

  },
  iconClock: {
    width: 16,
    height: 16,
  },
  timepickerButton: {
    margin: -16,
    color: theme.palette.primary.A400,
  },
  timepickerButtonSizeSm: {
    margin: -20,
  },
  inputAdornmentinputAdornmentLgOnly: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
    },
  },
});

class TimepickerField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      timepickerOpen: false,
      disableTimepickerTabFocus: false,
    };

    this.onTimeChange = this.onTimeChange.bind(this);
    this.onTextFieldFocus = this.onTextFieldFocus.bind(this);
    this.onTextFieldBlur = this.onTextFieldBlur.bind(this);
    this.onTimepickerClickAway = this.onTimepickerClickAway.bind(this);
    this.onTimepickerOpen = this.onTimepickerOpen.bind(this);
    this.onTimepickerClose = this.onTimepickerClose.bind(this);
    this.onTimepickerBlur = this.onTimepickerBlur.bind(this);
    this.onTextFieldKeyDown = this.onTextFieldKeyDown.bind(this);

    this.justOpenedTimepicker = false;
    this.justClosedTimepicker = false;
    this.timeoutDelay = 300;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.meta.active
      && nextProps.meta.valid
      && document.activeElement === this.inputRef) {
      this.setState({ disableTimepickerTabFocus: true });
    } else {
      this.setState({ disableTimepickerTabFocus: false });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.closeTimepickerTimeout);
    clearTimeout(this.openTimepickerTimeout);
  }

  onTimeChange(time) {
    this.props.input.onChange(time);
  }

  onTimepickerOpen() {
    this.justOpenedTimepicker = true;
    this.openTimepickerTimeout = setTimeout(() => {
      this.justOpenedTimepicker = false;
    }, this.timeoutDelay);
    this.setState({ timepickerOpen: true });
    this.props.onTimepickerOpen(this.props.input.value);
  }

  onTimepickerClose() {
    this.justClosedTimepicker = true;
    this.closeTimepickerTimeout = setTimeout(() => {
      this.justClosedTimepicker = false;
    }, this.timeoutDelay);
    this.setState({ timepickerOpen: false });
    if (this.props.input.value) {
      this.props.onTimepickerClose(this.props.input.value);
    }
  }

  onTextFieldFocus(event) {
    if (!this.justClosedTimepicker) {
      this.props.onFocus(event);
      this.onTimepickerOpen();
    }
  }

  onTextFieldBlur(event) {
    setTimeout(() => {
      const timepickerFocused = document.activeElement === document.body
        || (this.timepickerRef
        && isDescendant(this.timepickerRef, document.activeElement));
      if (!timepickerFocused) {
        this.props.onBlur(event);
        this.onTimepickerClose();
      }
    }, this.timeoutDelay);
  }

  onTimepickerClickAway() {
    if (!this.justOpenedTimepicker) {
      this.onTimepickerClose();
      if (this.props.input) {
        this.props.input.onBlur();
      }
    }
  }

  onTimepickerBlur() {
    setTimeout(() => {
      const timepickerFocused = this.timepickerRef
        && isDescendant(this.timepickerRef, document.activeElement);
      if (!timepickerFocused) {
        this.onTimepickerClose();
      }
    }, this.timeoutDelay);
  }

  onTextFieldKeyDown(event) {
    if (event.key === 'ArrowDown') {
      const onArrowDownClick = () => {
        if (this.timepickerRef) {
          const firstFocusableElement = this.timepickerRef
            .querySelector('button:not([disabled]), input:not([disabled])');
          firstFocusableElement.focus();
        }
      };
      this.setState({ disableTimepickerTabFocus: false });
      if (this.state.timepickerOpen) {
        onArrowDownClick();
      } else {
        this.onTimepickerOpen();
        setTimeout(() => {
          onArrowDownClick();
        }, this.timeoutDelay);
      }
    }
  }

  render() {
    const {
      classes,
      size,
      startIcon,
      endIcon,
      timepickerPlacement,
      inputAdornmentLgOnly,
      ...otherProps
    } = this.props;
    const { timepickerOpen, disableTimepickerTabFocus } = this.state;
    let verticalPlacement = 'bottom';
    let horizontalPlacement = otherProps.horizontalPlacement;
    const rootDomRect = this.rootRef ? this.rootRef.getBoundingClientRect() : null;
    const timepickerDomRect = this.timepickerRef
      ? this.timepickerRef.getBoundingClientRect() : null;
    const timepickerHeight = timepickerDomRect
      ? timepickerDomRect.height : 149;
    const timepickerWidth = timepickerDomRect
      ? timepickerDomRect.width : 210;
    if (rootDomRect) {
      if (((window.innerHeight - rootDomRect.top - rootDomRect.height)
        < timepickerHeight) && (timepickerHeight <= rootDomRect.top)) {
        verticalPlacement = 'top';
      }
      if (window.innerHeight === document.body.scrollHeight
        && ((window.innerHeight - rootDomRect.top - rootDomRect.height)
          < timepickerHeight)
        && (timepickerHeight >= rootDomRect.top)) {
        verticalPlacement = 'middle';
      }
      if (window.innerWidth - rootDomRect.left < timepickerWidth) {
        horizontalPlacement = 'right';
      }
    }
    const placement = timepickerPlacement || `${verticalPlacement}-${horizontalPlacement}`;
    return (
      <div
        className={classes.root}
        ref={(rootRef) => {
          this.rootRef = rootRef;
        }}
      >
        <TextField
          size={size}
          inputRef={(inputRef) => {
            this.inputRef = inputRef;
          }}
          {...startIcon ? {
            startAdornment: (
              <InputAdornment
                className={classNames({
                  [classes.inputAdornmentinputAdornmentLgOnly]: inputAdornmentLgOnly,
                })}
                position="start"
              >
                <IconClock className={classes.iconClock} />
              </InputAdornment>
            ),
          } : {}}
          {...endIcon ? {
            endAdornment: (
              <InputAdornment
                className={classNames({
                  [classes.inputAdornmentinputAdornmentLgOnly]: inputAdornmentLgOnly,
                })}
                position="end"
              >
                <IconClock className={classes.iconClock} />
              </InputAdornment>
            ),
          } : {}}
          {...otherProps}
          onFocus={this.onTextFieldFocus}
          onBlur={this.onTextFieldBlur}
          onKeyDown={this.onTextFieldKeyDown}
        />
        {timepickerOpen &&
          <ClickAwayListener
            onClickAway={this.onTimepickerClickAway}
          >
            <RootRef
              rootRef={(timepickerRef) => {
                this.timepickerRef = timepickerRef;
              }}
            >
              <Timepicker
                time={otherProps.input.value}
                onTimeChange={this.onTimeChange}
                onBlur={this.onTimepickerBlur}
                placement={placement}
                disableTabFocus={disableTimepickerTabFocus}
              />
            </RootRef>
          </ClickAwayListener>
        }
      </div>
    );
  }
}

TimepickerField.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.string,
  }).isRequired,
  meta: PropTypes.shape({
    active: PropTypes.bool,
    valid: PropTypes.bool,
  }).isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  startIcon: PropTypes.bool,
  endIcon: PropTypes.bool,
  onTimepickerOpen: PropTypes.func,
  onTimepickerClose: PropTypes.func,
  timepickerPlacement: PropTypes.string,
  horizontalPlacement: PropTypes.oneOf(['left', 'right']),
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  inputAdornmentLgOnly: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    popperRoot: PropTypes.string.isRequired,
    textFields: PropTypes.string.isRequired,
    textField: PropTypes.string.isRequired,
    textFieldInputRoot: PropTypes.string.isRequired,
    iconClock: PropTypes.string.isRequired,
    timepickerButton: PropTypes.string.isRequired,
    inputAdornmentinputAdornmentLgOnly: PropTypes.string.isRequired,
  }).isRequired,
};

TimepickerField.defaultProps = {
  label: '',
  size: 'md',
  startIcon: false,
  endIcon: false,
  onTimepickerOpen: () => {},
  onTimepickerClose: () => {},
  timepickerPlacement: null,
  horizontalPlacement: 'left',
  onFocus: () => {},
  onBlur: () => {},
  inputAdornmentLgOnly: false,
};

export default withStyles(styles)(TimepickerField);



// WEBPACK FOOTER //
// ./src/components/fields/TimepickerField/TimepickerField.js